"use client";
import { Track } from "livekit-client";
import { useTranslations } from "next-intl";
import { ParticipantTile } from "./ParticipantTile";
import { resolveParticipantAvatarUrl } from "@client/utils/livekitParticipantMetadata";
import { memo, useMemo, useEffect, useRef } from "react";
import { TrackReferenceOrPlaceholder } from "@livekit/components-react";
import { Mic, MicOff, Video, VideoOff, ScreenShare, ScreenShareOff, Users, PhoneOff } from "lucide-react";

interface PipContentProps {
  tracks: TrackReferenceOrPlaceholder[];
  totalParticipantCount: number;
  isMicEnabled: boolean;
  isCameraEnabled: boolean;
  isScreenShareEnabled: boolean;
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onToggleScreenShare: () => void;
  onExpandParticipants?: () => void;
  onLeaveMeeting?: () => void;
  participantAvatarByIdentity?: ReadonlyMap<string, string>;
  localAvatarUrl?: string | null;
}

/**
 * PipContent - Renders a grid of ParticipantTiles inside the PiP window.
 * Optimized with memo and useMemo for performance.
 */
export const PipContent = memo(function PipContent({
  tracks,
  totalParticipantCount,
  isMicEnabled,
  isCameraEnabled,
  isScreenShareEnabled,
  onToggleMic,
  onToggleCamera,
  onToggleScreenShare,
  onExpandParticipants,
  onLeaveMeeting,
  participantAvatarByIdentity,
  localAvatarUrl
}: PipContentProps) {
  const t = useTranslations("pip");

  // Ref for controls timeout (for cleanup)
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    const timeoutRef = controlsTimeoutRef;
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Check if there's an active screen share
  const screenShareTrack = useMemo(() => {
    return tracks.find((t) => t.source === Track.Source.ScreenShare);
  }, [tracks]);

  // Determine which tracks to show
  const visibleTracks = useMemo(() => {
    // Priority: Screen Share -> then others
    if (screenShareTrack) {
      return [screenShareTrack]; // Only show screen share if active
    }

    // Normal grid logic
    const overflow = Math.max(0, totalParticipantCount - tracks.length);
    if (overflow > 0) {
      return tracks.slice(0, 3); // Show 3 tiles + 1 overflow indicator
    }
    return tracks.slice(0, 4); // Show up to 4 tiles
  }, [tracks, screenShareTrack, totalParticipantCount]);

  // Calculate overflow count for +N indicator
  const overflowCount = useMemo(() => {
    if (screenShareTrack) return 0; // No overflow tile when showing screen share
    return Math.max(0, totalParticipantCount - tracks.length);
  }, [totalParticipantCount, tracks.length, screenShareTrack]);

  // Memoize grid class calculation
  const gridClass = useMemo(() => {
    const trackCount = visibleTracks.length;
    if (trackCount <= 1) return "grid-cols-1 grid-rows-1";
    if (trackCount === 2) return "grid-cols-2 grid-rows-1";
    return "grid-cols-2 grid-rows-2";
  }, [visibleTracks.length]);

  return (
    <div className="flex flex-col w-full h-full bg-[#111111] relative overflow-hidden group">
      {/* Grid Container */}
      <div className={`grid w-full h-full gap-1 p-1 ${gridClass}`}>
        {visibleTracks.length > 0 ? (
          <>
            {visibleTracks.map((track, index) => (
              <div
                key={track.participant.identity + index}
                className={`relative w-full h-full overflow-hidden rounded-lg bg-gray-900 border border-gray-800 ${
                  visibleTracks.length === 3 && overflowCount === 0 && index === 2 ? "col-span-2 w-1/2 mx-auto" : ""
                }`}
              >
                <ParticipantTile
                  track={track}
                  isLocal={track.participant.isLocal}
                  avatarUrl={resolveParticipantAvatarUrl(
                    track.participant,
                    participantAvatarByIdentity,
                    localAvatarUrl,
                    track.participant.isLocal
                  )}
                  isHandRaised={false}
                  onPin={() => {}}
                  isPinned={false}
                  onToggleSettings={undefined}
                  disableHoverControls={true}
                />
              </div>
            ))}
            {/* +N More Indicator */}
            {overflowCount > 0 && (
              <div
                onClick={onExpandParticipants}
                className={`relative w-full h-full overflow-hidden rounded-lg bg-gray-900 border border-gray-800 flex items-center justify-center transition-all ${
                  onExpandParticipants ? "cursor-pointer hover:bg-gray-800" : ""
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                    <Users className="w-5 h-5 text-gray-300" />
                  </div>
                  <span className="text-white text-sm font-medium">
                    {t("moreParticipants", { count: overflowCount })}
                  </span>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center text-white/50 col-span-full">{t("noActiveParticipant")}</div>
        )}
      </div>

      {/* Custom Controls Overlay (Glass Effect) */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center justify-center gap-3 px-4 py-2 backdrop-blur-md rounded-full shadow-lg z-50 pointer-events-auto transition-all duration-200 border border-white/10 opacity-0 group-hover:opacity-100">
        {/* Mic Toggle */}
        <button
          onClick={onToggleMic}
          onKeyDown={(e) => e.key === "Enter" && onToggleMic()}
          className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 ${
            isMicEnabled ? "bg-white/10 hover:bg-white/20 text-white" : "bg-[#EA4335] hover:bg-[#D93025] text-white"
          }`}
          title={isMicEnabled ? t("toggleMicOff") : t("toggleMicOn")}
          aria-label={isMicEnabled ? t("toggleMicOff") : t("toggleMicOn")}
        >
          {isMicEnabled ? <Mic size={18} /> : <MicOff size={18} />}
        </button>

        {/* Camera Toggle */}
        <button
          onClick={onToggleCamera}
          onKeyDown={(e) => e.key === "Enter" && onToggleCamera()}
          className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 ${
            isCameraEnabled ? "bg-white/10 hover:bg-white/20 text-white" : "bg-[#EA4335] hover:bg-[#D93025] text-white"
          }`}
          title={isCameraEnabled ? t("toggleCameraOff") : t("toggleCameraOn")}
          aria-label={isCameraEnabled ? t("toggleCameraOff") : t("toggleCameraOn")}
        >
          {isCameraEnabled ? <Video size={18} /> : <VideoOff size={18} />}
        </button>

        {/* Screen Share Toggle */}
        <button
          onClick={onToggleScreenShare}
          onKeyDown={(e) => e.key === "Enter" && onToggleScreenShare()}
          className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 ${
            isScreenShareEnabled
              ? "bg-[#1A73E8] hover:bg-[#1557B0] text-white"
              : "bg-white/10 hover:bg-white/20 text-white"
          }`}
          title={isScreenShareEnabled ? t("stopSharing") : t("startSharing")}
          aria-label={isScreenShareEnabled ? t("stopSharing") : t("startSharing")}
        >
          {isScreenShareEnabled ? <ScreenShareOff size={18} /> : <ScreenShare size={18} />}
        </button>

        {/* Divider */}
        <div className="w-px h-5 bg-white/20" />

        {/* Leave Meeting Button */}
        {onLeaveMeeting && (
          <button
            onClick={onLeaveMeeting}
            onKeyDown={(e) => e.key === "Enter" && onLeaveMeeting()}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 bg-[#EA4335] hover:bg-[#D93025] text-white"
            title={t("leaveMeeting")}
            aria-label={t("leaveMeeting")}
          >
            <PhoneOff size={18} />
          </button>
        )}
      </div>
    </div>
  );
});
